import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AuthShell from '../components/AuthShell';
import ChildSetupForm from '../components/ChildSetupForm';
import { createChildWithAssignments } from '../lib/createChild';
import { supabase } from '../lib/supabaseClient';

const { FiArrowRight } = FiIcons;

function Setup({
  existingHouseholdId,
  onComplete,
  userId
}) {
  const [step, setStep] = useState(existingHouseholdId ? 2 : 1);
  const [householdId, setHouseholdId] = useState(existingHouseholdId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!existingHouseholdId) return;
    setHouseholdId(existingHouseholdId);
    setStep(2);
  }, [existingHouseholdId]);

  const createHousehold = async () => {
    const { data, error: insertError } = await supabase
      .from('households')
      .insert({ owner_id: userId })
      .select('id')
      .single();

    if (!insertError) return data.id;

    if (insertError.code === '23505') {
      const { data: existing, error: fetchError } = await supabase
        .from('households')
        .select('id')
        .eq('owner_id', userId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!existing) throw insertError;
      return existing.id;
    }

    throw insertError;
  };

  const handleHouseholdContinue = async () => {
    setError('');
    setBusy(true);

    try {
      const nextHouseholdId = householdId || await createHousehold();
      setHouseholdId(nextHouseholdId);
      setStep(2);
    } catch (caught) {
      setError(caught.message || 'Could not create your household.');
    } finally {
      setBusy(false);
    }
  };

  const handleChildSubmit = async ({ name, grade }) => {
    setError('');
    setBusy(true);

    try {
      // First-time setup may still need a household; never create one when
      // existingHouseholdId was already provided (e.g. resume after partial setup).
      const nextHouseholdId = householdId || await createHousehold();
      setHouseholdId(nextHouseholdId);

      await createChildWithAssignments({
        householdId: nextHouseholdId,
        name,
        grade
      });

      await onComplete();
    } catch (caught) {
      setError(caught.message || 'Could not finish setup.');
    } finally {
      setBusy(false);
    }
  };

  if (step === 1) {
    return (
      <AuthShell
        kicker="Household setup"
        stepLabel="Step 1 of 2"
        subtitle="We’ll attach your account to a household so you can add a child next. No extra details needed."
        title="This is your Quantix household"
      >
        {error ? (
          <p className="mb-4 rounded-xl bg-[#fff0ea] px-3 py-2 text-sm text-[#ba503c]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end">
          <button
            className="flex items-center gap-2 rounded-xl bg-[#7c3aed] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#5b21b6] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={handleHouseholdContinue}
            type="button"
          >
            {busy ? 'Creating…' : 'Continue'}
            {!busy && <SafeIcon icon={FiArrowRight} />}
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker="First child"
      stepLabel="Step 2 of 2"
      subtitle="Add the first learner in this household. You can add more children later."
      title="Who is learning with Quantix?"
    >
      <ChildSetupForm
        busy={busy}
        error={error}
        submitBusyLabel="Saving…"
        submitLabel="Start using Quantix"
        onSubmit={handleChildSubmit}
      />
    </AuthShell>
  );
}

export default Setup;
